import { __ } from '@wordpress/i18n';
import { useState, useEffect } from '@wordpress/element';
import { PanelBody, RangeControl,   __experimentalToolsPanel as ToolsPanel,
  __experimentalToolsPanelItem as ToolsPanelItem, } from '@wordpress/components';
import { MediaUpload, MediaUploadCheck} from '@wordpress/block-editor';
import { SlideButton } from '@vayu-blocks/global';
import { VayuBlocksToggleControl , VayuBlocksSelectControl, VayuBlocksAlignment, VayuBlocksDropShadow, HeightControl} from '@vayu-blocks';

const Lottie = ({ attributes, setAttributes, openPanels, onTogglePanel }) => {
	const [error, setError] = useState('');

	useEffect(() => {
		const fetchLottieJson = async () => {
			if (attributes.lottietype === 'url' && attributes.lottieUrl) {
			
				try {
					const res = await fetch(attributes.lottieUrl);
					if (!res.ok) throw new Error('Failed to load JSON');

					const json = await res.json();
					setAttributes({ lottieData: json, totaltime: json.op || 100, });
					setError('');
				} catch (err) {
					setError(__('Failed to load Lottie JSON from URL', 'vayu-blocks'));
					setAttributes({ lottieData: null });
				}
			}
		};

		fetchLottieJson();
	}, [attributes.lottieUrl]);

	const handleTriggerChange = (key, value) => {
		setAttributes({
			trigger: {
				...attributes.trigger,
				[key]: value,
			},
		});
	};

	return (
		<>
			<PanelBody
				title={__('Lottie', 'vayu-blocks')}
				opened={openPanels['lottie-panel']}
				onToggle={() => onTogglePanel('lottie-panel')}
			>
				<div className="vb-lottie-button-wrapper">
					<SlideButton
						button={{ media: 'Media File', url: 'External URL' }}
						onChange={(value) =>
							setAttributes({ lottietype: value})
						}
						defaultValue={attributes.lottietype}
					/>
				</div>

				<div class="vb-lottie-helper">
					<p>
						Browse animations to find the perfect one for your design.
						<a
						href="https://lottiefiles.com/"
						target="_blank"
						rel="noopener noreferrer"
						class="vb-lottie-icon-link"
						title="Visit LottieFiles"
						>
						<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 400 400">
							<path fill="#6c1bc3" d="M304.035 89.598c-68.951 0-94.382 49.158-114.885 88.663l-13.486 25.248c-21.74 41.899-37.964 67.35-79.781 67.35a19.756 19.756 0 0 0-13.995 5.784 19.769 19.769 0 0 0-4.291 21.56 19.758 19.758 0 0 0 10.713 10.702 19.756 19.756 0 0 0 7.573 1.499c68.951 0 94.382-49.158 114.885-88.663l13.405-25.248c21.74-41.899 37.964-67.35 79.781-67.35a19.763 19.763 0 0 0 18.286-12.201 19.77 19.77 0 0 0-4.291-21.56 19.763 19.763 0 0 0-13.995-5.784z"/>
						</svg>
						</a>
					</p>
				</div>

				<div className="vb-lottie-select-wrapper">
					{attributes.lottietype === 'media' && (
						<div className="vb-lottie-media-upload">
							<MediaUploadCheck>
								<MediaUpload
									onSelect={(media) => {
										if (media && media.url) {
											fetch(media.url)
												.then((res) => {
													if (!res.ok) throw new Error('Failed to load JSON');
													return res.json();
												})
												.then((json) => {
													setAttributes({
														lottieData: json,
														lottieMediaId: media.id,
														lottieUrl: media.url,
														lottieFileName: media.filename || media.title || 'Lottie File',
														totaltime: json.op || 100,
													});
													setError('');
												})
												.catch(() => {
													setError(__('Failed to load Lottie JSON from media file', 'vayu-blocks'));
													setAttributes({ lottieData: null });
												});
										}
									}}
									allowedTypes={['application/json']}
									value={attributes.lottieMediaId}
									render={({ open }) => (
										<div className="vb-lottie-buttons-wrapper">
											{attributes.lottieMediaId && attributes.lottieFileName && (
												<div className="vb-lottie-file-name">{attributes.lottieFileName}</div>
											)}

											<div className="vb-lottie-buttons">
												<button className={`vb-lottie-btn ${
													attributes.lottieMediaId ? 'vb-lottie-change-btn' : 'vb-lottie-upload-btn'
													}`} onClick={open}
												>
													{attributes.lottieMediaId
														? __('Change', 'vayu-blocks')
														: __('Upload Lottie JSON', 'vayu-blocks')}
												</button>

												{attributes.lottieMediaId && (
													<button
														className="vb-lottie-btn"
														onClick={() => {
															setAttributes({
																lottieData: null,
																lottieMediaId: null,
																lottieUrl: '',
																lottieFileName: '',
															});
														}}
													>
														{__('Delete', 'vayu-blocks')}
													</button>
												)}
											</div>
										</div>
									)}
								/>
							</MediaUploadCheck>
						</div>
					)}

					
					{attributes.lottietype === 'url' && (
						<div className="vb-lottie-url-wrapper">
							<input
								type="text"
								className="vb-lottie-url-input"
								value={attributes.lottieUrl || ''}
								onChange={(e) => setAttributes({ lottieUrl: e.target.value })}
								placeholder="https://yourdomain.com/animation.json"
							/>
							<p className="vb-lottie-url-help" style={{ color: '#a3a2a282' }}>
								{__('Paste a direct URL to a valid .json Lottie file.', 'vayu-blocks')}
							</p>

							{(attributes.lottieMediaId || attributes.lottieUrl) && (
								<button
									className="vb-lottie-btn vb-lottie-url-delete-btn"
									onClick={() => {
										setAttributes({
											lottieData: null,
											lottieMediaId: null,
											lottieUrl: '',
											lottieFileName: '',
										});
									}}
								>
									{__('Delete', 'vayu-blocks')}
								</button>
							)}
						</div>
					)}


					{error && (
						<div className="vb-lottie-error">
							<span>{error}</span>
						</div>
					)}
				</div>

				{(attributes.lottieMediaId || attributes.lottieUrl) && (
				<>
					<HeightControl
						label={__('Width', 'vayu-blocks')}
						value={ attributes.width }
						onChange={ (value) => setAttributes({ width: value }) }
						responsive={true}
					/> 

					<br/>

					<VayuBlocksAlignment
						onChange={(key,value)=>setAttributes({[key]:value})}
						alignment = {{key:'alignment',value:attributes.alignment}}
					/>

					<RangeControl
						className='vb-range-chnage-direction'
						initialPosition={0}
						label={__('Rotate', 'vayu-blocks')}
						max={360}
						min={0}
						value={attributes.rotation}
						onChange={(value) => setAttributes({rotation:value})}
					/>

					<VayuBlocksDropShadow 
						onChange={(key,value)=>setAttributes({[key]:value})}
						dropShadow={{key:'dropshadow',value:attributes.dropshadow}}
					/>
				</>
				)}

			</PanelBody>

			<PanelBody
				title={__('Settings', 'vayu-blocks')}
				opened={openPanels['lottie-settings']}
				onToggle={() => onTogglePanel('lottie-settings')}
			>

				<VayuBlocksSelectControl 
					label={__('Trigger', 'vayu-blocks')}
					value={attributes.trigger?.type}
					onChange={(value) => handleTriggerChange('type', value)}
					options={[
						{ label: __('None', 'vayu-blocks'), value: 'none' },
						{ label: __('Viewport', 'vayu-blocks'), value: 'viewport' },
						{ label: __('On Click', 'vayu-blocks'), value: 'click' },
						{ label: __('On Hover', 'vayu-blocks'), value: 'hover' },
						{ label: __('Scroll', 'vayu-blocks'), value: 'scroll' },
					]}
				/>

				{(attributes.trigger?.type === 'viewport' || attributes.trigger?.type === 'scroll') && (
					<ToolsPanel
						label={__('Viewport', 'vayu-blocks')}
						className="vayu-tools-lottie-viewport"
					>
						<ToolsPanelItem
							hasValue={() => (attributes.triiger?.top !== undefined && attributes.trigger?.top !== 100)}
							label={__('Top', 'vayu-blocks')}
							onDeselect={() => handleTriggerChange('top', undefined)}
							isShownByDefault={false}
						>
							<RangeControl
								__next40pxDefaultSize={true}
								__nextHasNoMarginBottom={true}
								label={__('Top', 'vayu-blocks')}
								value={attributes.trigger?.top}
								onChange={(value) => {
									if (value <= attributes.trigger?.bottom) return;
									handleTriggerChange('top', value)
								}}
								min={attributes.trigger?.bottom}
								max={100}
								step={1}
								allowReset={true}
								withInputField={false}
								resetFallbackValue={0}
								showTooltip
							/>
						</ToolsPanelItem>

						<ToolsPanelItem
							hasValue={() => (attributes.trigger?.bottom !== undefined && attributes.trigger?.bottom !== 0) }
							label={__('Bottom', 'vayu-blocks')}
							onDeselect={() => handleTriggerChange('bottom', undefined)}
							isShownByDefault={false}
						>
							<RangeControl
								__next40pxDefaultSize={true}
								__nextHasNoMarginBottom={true}
								label={__('Bottom', 'vayu-blocks')}
								value={attributes.trigger?.bottom}
								onChange={(value) => {
									if (value >= attributes.trigger?.top) return;
									handleTriggerChange('bottom', value)
								}}
								min={0}
								max={attributes.trigger?.top}
								step={1}
								allowReset={true}
								withInputField={false}
								resetFallbackValue={0}
								showTooltip
							/>

						</ToolsPanelItem>
					</ToolsPanel>
				)}

				<RangeControl
					__next40pxDefaultSize = {true}
					__nextHasNoMarginBottom ={true}
					label ={__('Play Speed', 'vayu-blocks')}
					value = {attributes.speed}
					onChange = {(value)=> setAttributes({speed:value})}
					max={5}
					min={ 0 }
					step={0.1}
					allowReset={true}
					withInputField={true}
				/>

				<VayuBlocksToggleControl 
					label ={__('Reverse Direction', 'vayu-blocks')}
					checked = {attributes.direction}
					onChange = {(value)=> setAttributes({direction:value})}
				/>

				{/* <VayuBlocksToggleControl 
					label ={__('Autoplay', 'vayu-blocks')}
					checked = {attributes.autoplay}
					onChange = {(value)=> setAttributes({autoplay:value})}
				/> */}

				{attributes.trigger?.type != 'viewport' && (
					<>
						<VayuBlocksToggleControl 
							label ={__('Loop', 'vayu-blocks')}
							checked = {attributes.loop}
							onChange = {(value)=> setAttributes({loop:value})}
						/>
						<p style={{ fontSize: '13px', color: '#888'}}>
						{__('Autoplay and Loop will only work on the frontend.', 'vayu-blocks')}
						</p>
					</>
				)}

				<ToolsPanel
					label={__('Time Control', 'vayu-blocks')}
					className="vayu-tools-lottie-viewport"
				>
					<ToolsPanelItem
						hasValue={() => attributes.start !== undefined}
						label={__('Start Point', 'vayu-blocks')}
						onDeselect={() => setAttributes({ start: undefined })}
						isShownByDefault={false}
					>
						<RangeControl
							__next40pxDefaultSize={true}
							__nextHasNoMarginBottom={true}
							label={__('Start Point', 'vayu-blocks')}
							value={attributes.start}
							onChange={(value) => setAttributes({ start: value })}
							min={0}
							max={
								Math.min(
									parseFloat(attributes.end ?? attributes.totaltime ?? 100),
									parseFloat(attributes.totaltime ?? 100)
								)
							}
							step={1}
							allowReset={true}
							withInputField={true}
						/>
					</ToolsPanelItem>

					<ToolsPanelItem
						hasValue={() => attributes.end !== undefined}
						label={__('End Point', 'vayu-blocks')}
						onDeselect={() => setAttributes({ end: undefined })}
						isShownByDefault={false}
					>
						<RangeControl
							__next40pxDefaultSize={true}
							__nextHasNoMarginBottom={true}
							label={__('End Point', 'vayu-blocks')}
							value={attributes.end}
							onChange={(value) => {
								if (typeof attributes.start === 'number' && value <= attributes.start) return;
								setAttributes({ end: value });
							}}
							min={attributes.start || 0}
							max={attributes.totaltime || 100}
							step={1}
							allowReset={true}
							withInputField={true}
						/>
					</ToolsPanelItem>
				</ToolsPanel>

			</PanelBody>
		</>
	);
};

export default Lottie;